import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Suspense, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import styled from '@emotion/styled';
import { useIntl } from 'react-intl-next';
import Button, { LinkButton } from '@atlaskit/button/new';
import DepartmentIcon from '@atlaskit/icon/core/department';
import Link from '@atlaskit/link';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { descriptionHelper } from '../ai-answer-dialog/ai-answer-body/ai-answer-result/json/recent-work-list/description-helper';
import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { ErrorBoundary } from '../common/ui/error-boundary';
import { messages } from '../messages';
import { onKnowledgeCardLinkClick } from './analytics';
import { KnowledgeCardsRow } from './knowledge-cards-row';
import { PersonCard } from './person-card';
import { SeparatorLine } from './separator-line';
import { EmptyStateText, KnowledgeCardsTeamCountText, KnowledgeDefaultSectionHeader, KnowledgeDefaultText, KnowledgeIconWrapper } from './styled';
import { KnowledgeCardsIntentType, KnowledgeCardsRowType } from './types';
import { trimARI } from './util';
var BodyContainer = xcss({
  position: 'relative'
});
var MutedText = xcss({
  color: 'color.text.subtlest',
  fontWeight: 'font.weight.regular'
});
var EmptyState = function EmptyState(_ref) {
  var text = _ref.text;
  return /*#__PURE__*/React.createElement(Box, {
    paddingInline: "space.200",
    paddingBlock: "space.075"
  }, /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(EmptyStateText, null, text)));
};
var HoverCard = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_.smart-card/hover-card" */'@atlaskit/smart-card/hover-card').then(function (_ref2) {
    var HoverCard = _ref2.HoverCard;
    return {
      default: HoverCard
    };
  });
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-unsafe-style-overrides
var CardContainer = styled(TooltipPrimitive)({
  width: '214px',
  height: '236px'
});
var EXPANDED_MAX_ITEMS_SHOWN = 6;
var MAX_ITEMS_SHOWN = 3;
export var KnowledgeCardsBody = /*#__PURE__*/React.memo(function (_ref3) {
  var type = _ref3.type,
    currentUserId = _ref3.currentUserId,
    recommendedUsers = _ref3.recommendedUsers,
    recentActivity = _ref3.recentActivity,
    profileUrl = _ref3.profileUrl,
    viewAllUrl = _ref3.viewAllUrl;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    searcherId = _useAIAnswerContext.userDetails.id;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage,
    formatDate = _useIntl.formatDate;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setExpanded = _useState2[1];
  var isPerson = type === KnowledgeCardsIntentType.PERSON;
  var backupViewAllUrl = "https://hello.atlassian.net/people/".concat(currentUserId, "/work");
  return /*#__PURE__*/React.createElement(Box, {
    xcss: BodyContainer
  }, /*#__PURE__*/React.createElement(Stack, {
    space: "space.025"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.200"
  }, /*#__PURE__*/React.createElement(Stack, {
    grow: "fill",
    space: "space.075"
  }, /*#__PURE__*/React.createElement(Box, {
    paddingInlineStart: "space.200",
    paddingBlockStart: "space.150",
    paddingInlineEnd: "space.300",
    paddingBlockEnd: "space.075"
  }, /*#__PURE__*/React.createElement(Inline, {
    spread: "space-between",
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(KnowledgeDefaultSectionHeader, null, formatMessage(messages.knowledge_cards_recent_work_subheader)), recentActivity.length > 0 && /*#__PURE__*/React.createElement(Link, {
    href: viewAllUrl || backupViewAllUrl,
    appearance: "subtle",
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'recent-work-view-all'));
      fireSAINUsageEvent({
        metCriteria: SainAction.RECENT_WORK_CLICKED
      });
    }
  }, /*#__PURE__*/React.createElement(KnowledgeDefaultText, null, formatMessage(messages.knowledge_cards_view_all_link))))), /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(Provider, null, !recentActivity.length ? /*#__PURE__*/React.createElement(EmptyState, {
    text: formatMessage(messages.knowledge_cards_recent_work_empty)
  }) : recentActivity.slice(0, !isExpanded ? MAX_ITEMS_SHOWN : Math.min(recentActivity.length, EXPANDED_MAX_ITEMS_SHOWN)).map(function (activity) {
    return /*#__PURE__*/React.createElement(HoverCard, {
      url: activity.url,
      key: "knowledge-card-activity-".concat(activity.url)
    }, /*#__PURE__*/React.createElement(KnowledgeCardsRow, {
      primaryText: activity.title,
      secondaryText: "".concat(activity.containerName, " \u2022 ").concat(descriptionHelper({
        currentUserId: searcherId || '',
        event: activity.event,
        contributors: activity.contributors,
        othersCount: activity.othersCount,
        formatMessage: formatMessage,
        formatDate: formatDate,
        timestamp: new Date(activity.timestamp)
      })),
      pictureUrl: activity.iconUrl,
      type: KnowledgeCardsRowType.WORK,
      onClick: function onClick() {
        fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'recent-work-item'));
        fireSAINUsageEvent({
          metCriteria: SainAction.RECENT_WORK_CLICKED
        });
        window.open(activity.url, '_self', 'noopener, noreferrer');
      }
    }));
  }))))), /*#__PURE__*/React.createElement(Stack, {
    grow: "fill",
    space: "space.075"
  }, /*#__PURE__*/React.createElement(Box, {
    paddingInlineStart: "space.200",
    paddingBlockStart: isPerson ? 'space.100' : 'space.200',
    paddingInlineEnd: "space.300",
    paddingBlockEnd: isPerson ? 'space.025' : 'space.075'
  }, /*#__PURE__*/React.createElement(Inline, {
    spread: "space-between",
    alignBlock: "center"
  }, isPerson ? /*#__PURE__*/React.createElement(KnowledgeDefaultSectionHeader, null, formatMessage(messages.knowledge_cards_collaborators_subheader)) : /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(KnowledgeDefaultSectionHeader, null, formatMessage(messages.knowledge_cards_team_members_subheader)), /*#__PURE__*/React.createElement(KnowledgeCardsTeamCountText, null, recommendedUsers.length)), isPerson && /*#__PURE__*/React.createElement(KnowledgeIconWrapper, null, /*#__PURE__*/React.createElement(Link, {
    href: "https://hello.atlassian.net/people/".concat(currentUserId || '', "#workswith"),
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'reporting-lines-link'));
      fireSAINUsageEvent({
        metCriteria: SainAction.REPORTING_LINES_CLICKED
      });
    }
  }, /*#__PURE__*/React.createElement(DepartmentIcon, {
    label: "department",
    color: "var(--ds-text, #172B4D)"
  }))))), !recommendedUsers.length ? /*#__PURE__*/React.createElement(EmptyState, {
    text: formatMessage(isPerson ? messages.knowledge_cards_collaborators_empty : messages.knowledge_cards_teammembers_empty)
  }) : recommendedUsers.slice(0, !isExpanded ? MAX_ITEMS_SHOWN : Math.min(recommendedUsers.length, EXPANDED_MAX_ITEMS_SHOWN)).map(function (user) {
    var userUrl = "/people/".concat(trimARI(user.accountId || ''));
    return /*#__PURE__*/React.createElement(Tooltip, {
      key: user.accountId,
      position: "bottom-end"
      // NOTE: In React 18, `styled(TooltipPrimitive)` does not work with this type
      // `styled(TooltipPrimitive)` may be better written as `(props: …) => <TooltipPrimitive css={…} {...props}>`
      ,
      component: CardContainer,
      content: /*#__PURE__*/React.createElement(PersonCard, {
        key: user.accountId,
        name: user.name,
        profilePicUrl: user.picture || '',
        jobTitle: user.title,
        profileUrl: user.url || userUrl,
        email: user.email
      })
    }, function (triggerProps) {
      return /*#__PURE__*/React.createElement(KnowledgeCardsRow, {
        primaryText: user.name,
        secondaryText: !!user.title || !!user.department ? [user.title, user.department].filter(function (str) {
          return !!str;
        }).join(', ') : '',
        pictureUrl: user.picture || '',
        type: KnowledgeCardsRowType.PERSON,
        triggerProps: triggerProps,
        onClick: function onClick() {
          fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'user-list-item'));
          fireSAINUsageEvent({
            metCriteria: SainAction.TEAM_MEMBER_CLICKED
          });
          window.open(user.url || userUrl, '_self', 'noopener, noreferrer');
        }
      });
    });
  }))), /*#__PURE__*/React.createElement(Box, {
    paddingInline: "space.200",
    paddingBlockStart: "space.050",
    paddingBlockEnd: "space.075"
  }, recommendedUsers.length > MAX_ITEMS_SHOWN || recentActivity.length > MAX_ITEMS_SHOWN ? /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    space: "space.025"
  }, /*#__PURE__*/React.createElement(SeparatorLine, null), isExpanded ? /*#__PURE__*/React.createElement(LinkButton, {
    href: profileUrl || '',
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'footer-view-all'));
      fireSAINUsageEvent({
        metCriteria: isPerson ? SainAction.VIEW_PROFILE_CLICKED : SainAction.VIEW_TEAM_PAGE_CLICKED
      });
    }
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: MutedText
  }, isPerson ? formatMessage(messages.ai_answer_who_questions_view_profile) : formatMessage(messages.knowledge_cards_view_team))) : /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      setExpanded(function (e) {
        return !e;
      });
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'show-more'));
      fireSAINUsageEvent({
        metCriteria: SainAction.EXPAND_CLICKED
      });
    }
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: MutedText
  }, formatMessage(messages.ai_answer_expand_button_label))), /*#__PURE__*/React.createElement(SeparatorLine, null)) : null)));
});